/* eslint-disable no-confusing-arrow */
import WordChoice from 'es6/models/word-choice';

/**
 * View Model for the WordSlot component.
 *
 * Some WordSlot instances contain a flag, "isPunctuation." If it does,
 * the value cannot be changed and no word choices can be inserted into the
 * word slot.
 */
export default class WordSlot {
  constructor(data = {}) {
    Object.assign(this, {
      expected: '',
      graded: false,
      hinted: false,
      isPunctuation: false,
      wordChoice: null,
    }, data);
  }

  /**
   * Returns the value for the word slot.
   *
   * @returns {String}
   */
  get value() {
    return this.wordChoice ? this.wordChoice.value : '';
  }

  /**
   * Clears the word choice and value.
   *
   * @returns {WordChoice}
   */
  free() {
    this._throwFrozenError();

    // Store a reference for the return.
    const wordChoice = this.wordChoice;

    // Unset the existing values
    if (wordChoice) {
      wordChoice.free();
    }

    this.wordChoice = null;

    return wordChoice;
  }

  /**
   * Puts a word choice in the slot. Removes existing word choice from slot
   * if entered.
   *
   * @param {WordChoice}
   * @param {Boolean} isGraded    changes the graded status of the slot
   * @returns {WordChoice}        freed WordChoice (if there is one)
   */
  insert(wordChoice, isGraded = false) {
    this._throwFrozenError();

    if (!(wordChoice instanceof WordChoice)) {
      throw new Error('Can only insert WordChoice into a word slot.');
    }

    if (wordChoice.used) {
      throw new Error('Cannot insert a word choice that is being used.');
    }

    // Clear current value if it is available.
    const freedChoice = this.free();
    this.wordChoice = wordChoice;
    this.graded = isGraded;

    wordChoice.use();

    return freedChoice;
  }

  /**
   * Determines if the is fillable.
   *
   * @returns {Boolean}
   */
  isAvailable() {
    return this.wordChoice === null && !this.isFrozen() || this.graded && this.value !== this.expected;
  }

  /**
   * Determines if slot is correct with current word choice.
   *
   * @returns {Boolean}
   */
  isCorrect() {
    return this.value === this.expected;
  }

  /**
   * @returns {Boolean}
   */
  isCorrectAndGraded() {
    return this.isCorrect() && this.graded;
  }

  /**
   * @returns {Boolean}
   */
  isFilled() {
    return this.wordChoice !== null;
  }

  /**
   * Can this slot be cleared or have anything inserted?
   *
   * @returns {Boolean}
   */
  isFrozen() {
    return this.isPunctuation || this.isCorrectAndGraded();
  }

  /**
   * @returns {Boolean}
   */
  isIncorrect() {
    return this.value !== this.expected && !this.isPunctuation;
  }

  /**
   * @returns {Boolean}
   */
  isIncorrectAndGraded() {
    return this.isIncorrect() && this.graded;
  }

  /**
   * @returns {Boolean}
   */
  isIncorrectAndUnhinted() {
    return this.isIncorrect() && !this.hinted;
  }

  /**
   * Only throws an error if the object is a punctuation object.
   */
  _throwFrozenError() {
    if (this.isFrozen()) {
      throw new Error('Cannot manipulate this word slot.');
    }
  }

  /**
   * Gets the index of the word slot that will be shifted to.
   *
   * @param {WordSlot[]} wordSlots
   * @param {Number} startIndex      index of where the shift starts
   * @returns {Number}               index of the word slot that will be shifted to, -1 when not found
   */
  static getShiftEndIndex(wordSlots, startIndex) {
    const cantShift = (wordSlot) => wordSlot.isFrozen();
    const finished = (wordSlot) => !wordSlot.isFilled();

    // Try to the right.
    for (let i = startIndex; i < wordSlots.length; i++) {
      const wordSlot = wordSlots[i];

      if (finished(wordSlot)) {
        return i;
      }

      if (cantShift(wordSlot)) {
        break;
      }
    }

    // Try to the left.
    for (let i = startIndex; i >= 0; i--) {
      const wordSlot = wordSlots[i];

      if (finished(wordSlot)) {
        return i;
      }

      if (cantShift(wordSlot)) {
        break;
      }
    }

    return -1;
  }

  /**
   * Finds a WordSlot by WordChoice.
   *
   * @param {WordSlot[]} wordSlots
   * @param {WordChoice} wordChoice
   * @returns {WordSlot}
   */
  static findWordSlotByWordChoice(wordSlots, wordChoice) {
    return wordSlots.find(wordSlot => wordSlot.wordChoice === wordChoice);
  }

  /**
   * Shifts word slots over given a start position.
   *
   * @param {WordSlot[]} wordSlots     list of WordSlot objects
   * @param {Number} startIndex        index where the shifting starts
   */
  static shiftWordSlots(wordSlots, startIndex) {
    const endIndex = WordSlot.getShiftEndIndex(wordSlots, startIndex);
    let nextWordChoice;

    // Nothing can be shifted.
    if (endIndex === -1) {
      return;
    }

    const isShiftRight = endIndex > startIndex;

    const cond = (i) => isShiftRight ? i <= endIndex : i >= endIndex;

    for (let i = startIndex; cond(i); isShiftRight ? i++ : i--) {
      const wordSlot = wordSlots[i];

      // Insert the previous WordChoice.
      if (nextWordChoice) {
        nextWordChoice = wordSlot.insert(nextWordChoice);
      } else {
        nextWordChoice = wordSlot.free();
      }
    }
  }
}
